import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GeoDataProvider } from './contexts/GeoDataContext';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage/LandingPage';
import DashboardHome from './pages/DashboardHome';
import SubscriptionForm from './pages/SubscriptionForm';
import Upgrade from './pages/Upgrade';
import PaymentSuccess from './pages/PaymentSuccess';
import Settings from './pages/Settings';
import EmailVerification from './pages/EmailVerification';
import MagicLinkPage from './pages/MagicLinkPage';
import AdminPage from './pages/AdminPage';
import ImpersonatePage from './pages/ImpersonatePage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TelegramSubscriptionPage from './pages/TelegramSubscriptionPage';
import AGB from './pages/AGB';
import Datenschutz from './pages/Datenschutz';
import Impressum from './pages/Impressum';
import Widerruf from './pages/Widerruf';
import { UnifiedFeed } from './components/UnifiedFeed';
import Footer from './components/Footer';
import ServiceWorkerManager from './components/ServiceWorkerManager';
import MaintenanceBanner from './components/MaintenanceBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import DevIndicator from './components/DevIndicator';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import { UpgradeModal } from './components/UpgradeModal';
import { AppShell } from './components/shell/AppShell';
import { isShellPath, isTabBarHidden } from './components/shell/shellRoutes';
import { Spinner } from './components/ui';
import './App.css';

const LoadingScreen: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-page">
    <Spinner label="Lädt" />
    <span className="sr-only">Lädt…</span>
  </div>
);

const ConditionalFooter: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/tg') || pathname === '/') return null;
  // On mobile, AppShell routes reserve space above the fixed tab bar so the
  // footer stays reachable by scrolling instead of hiding behind it.
  const reserveTabBarSpace = isShellPath(pathname) && !isTabBarHidden(pathname);
  return (
    <Footer
      className={
        reserveTabBarSpace ? 'pb-[calc(var(--shell-tabbar-h)+env(safe-area-inset-bottom)+16px)] md:pb-4' : undefined
      }
    />
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/verify" element={<EmailVerification />} />
      <Route path="/auth/magic" element={<MagicLinkPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/widerruf" element={<Widerruf />} />
      <Route path="/impersonate" element={<ImpersonatePage />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route element={<AppShell allowAnonymous />}>
        <Route path="/feed" element={<UnifiedFeed />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/dashboard/subscription/new" element={<SubscriptionForm />} />
        <Route path="/dashboard/subscription/edit/:id" element={<SubscriptionForm />} />
        <Route path="/dashboard/upgrade" element={<Upgrade />} />
        <Route path="/dashboard/upgrade/success" element={<PaymentSuccess />} />
        <Route path="/dashboard/settings" element={<Settings />} />
        <Route path="/admin" element={<AdminPage />} />
      </Route>
      <Route path="/tg" element={<TelegramSubscriptionPage />} />
      <Route path="/upgrade" element={<Navigate to="/dashboard/upgrade" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <>
      <Router>
        <ThemeProvider>
          <ServiceWorkerManager />
          <DevIndicator />
          <AuthProvider>
            <GeoDataProvider>
              <UpgradeModalProvider>
                <div className="flex flex-col min-h-screen bg-page text-ink">
                  <MaintenanceBanner />
                  <ImpersonationBanner />
                  <div className="flex-grow">
                    <AppRoutes />
                  </div>
                  <ConditionalFooter />
                </div>
                <UpgradeModal />
              </UpgradeModalProvider>
            </GeoDataProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </>
  );
}

export default App;
